import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Card — MOMENTUM surface primitive.
 *
 * Uses the `surface-card` utility class (defined in index.css, shipped in
 * PR #163): rounded 16px, near-invisible border, subtle 2-layer shadow.
 * Reads as a floating plane against the cream/ink bg, not a bordered box.
 *
 * `text-card-foreground` retained so the existing text-color cascade still
 * applies inside cards.
 *
 * Pass `interactive` for clickable surfaces (workout/program/client tiles,
 * dashboard widgets that navigate). Adds canonical hover (border + tiny
 * lift), active (press-down), and focus-visible (volt ring) — replaces
 * the inconsistent hover:border-primary/40 + hover:shadow-md + cursor-
 * pointer combos scattered across pages.
 */
interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  interactive?: boolean;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, interactive, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "surface-card text-card-foreground transition-all duration-200",
        interactive && [
          "cursor-pointer",
          "hover:border-border hover:shadow-md hover:-translate-y-0.5",
          "active:translate-y-0 active:shadow-sm",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        ],
        className,
      )}
      {...props}
    />
  ),
);
Card.displayName = "Card";

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col space-y-1.5 p-6", className)} {...props} />
  ),
);
CardHeader.displayName = "CardHeader";

/**
 * CardTitle — uses the `section-title` utility (Clash Display 600) so every
 * card heading in the app picks up the display-font treatment from the
 * landing page without per-call changes. text-xl preserved as the size
 * default; callers can override with className.
 */
const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3 ref={ref} className={cn("section-title text-xl", className)} {...props} />
  ),
);
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
  ),
);
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />,
);
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props} />
  ),
);
CardFooter.displayName = "CardFooter";

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
